














































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import moment from "moment";
import { SupportDashboard } from "@/http/api/v2/supporter/dashboard";
import { SupportDashboardDto } from "@/http/dto/supportDashboard";

const HotelModule = namespace("HotelModule");

interface ChartPoint {
  date: string;
  count: number;
  x: number;
  y: number;
}

interface ChartLabel {
  date: string;
  text: string;
  left: number;
}

const emptyDashboard = (): SupportDashboardDto => ({
  periodDays: 30,
  periodStart: "",
  periodEnd: "",
  generatedTime: "",
  operatingHotelCount: 0,
  loginActiveHotelCount: 0,
  contentActiveHotelCount: 0,
  totalUserCount: 0,
  activeUserCount: 0,
  issuesToday: 0,
  issuesSevenDays: 0,
  issuesInPeriod: 0,
  noticesInPeriod: 0,
  dailyIssues: [],
  storage: {
    snapshotDate: "-",
    totalStorageBytes: 0,
    totalFileCount: 0,
    hotelCount: 0,
    topHotels: [],
  },
});

@Component
export default class SupportDashboardPage extends Vue {
  @HotelModule.State("hotelObjectId") hotelObjectId!: string;

  periodDays = 30;
  loading = false;
  errorMessage = "";
  data: SupportDashboardDto = emptyDashboard();

  get hotelObjectIdValue() {
    return this.hotelObjectId;
  }

  @Watch("hotelObjectIdValue")
  hotelObjectIdChanged() {
    this.load();
  }

  get hasDailyIssues() {
    return this.data.dailyIssues.length > 0 && this.data.issuesInPeriod > 0;
  }

  get chartMaximum() {
    const maximum = Math.max(...this.data.dailyIssues.map(item => item.count), 1);
    return Math.ceil(maximum / 10) * 10;
  }

  get chartPointObjects(): ChartPoint[] {
    const count = this.data.dailyIssues.length;
    const denominator = Math.max(count - 1, 1);

    return this.data.dailyIssues.map((item, index) => ({
      ...item,
      x: 30 + (index / denominator) * 960,
      y: 250 - (item.count / this.chartMaximum) * 230,
    }));
  }

  get chartPoints() {
    return this.chartPointObjects.map(point => `${point.x},${point.y}`).join(" ");
  }

  get chartAreaPoints() {
    if (this.chartPointObjects.length === 0) {
      return "";
    }

    return `30,250 ${this.chartPoints} 990,250`;
  }

  get chartLabels(): ChartLabel[] {
    const points = this.data.dailyIssues;
    if (points.length === 0) {
      return [];
    }

    const desiredLabels = Math.min(5, points.length);
    const indexes = new Set<number>();
    for (let index = 0; index < desiredLabels; index += 1) {
      indexes.add(
        desiredLabels === 1
          ? 0
          : Math.round((index / (desiredLabels - 1)) * (points.length - 1)),
      );
    }

    return Array.from(indexes).map(index => ({
      date: points[index].date,
      text: moment(points[index].date).format("M/D"),
      left: (index / Math.max(points.length - 1, 1)) * 100,
    }));
  }

  get generatedTimeText() {
    return moment(this.data.generatedTime).format("YYYY.MM.DD HH:mm");
  }

  async mounted() {
    await this.load();
  }

  number(value: number) {
    return Number(value || 0).toLocaleString("ko-KR");
  }

  formatBytes(value: number) {
    const bytes = Number(value || 0);
    if (bytes === 0) {
      return "0 B";
    }

    const units = ["B", "KB", "MB", "GB", "TB"];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / Math.pow(1024, unitIndex);
    return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${
      units[unitIndex]
    }`;
  }

  storagePercentage(value: number) {
    const maximum = Math.max(...this.data.storage.topHotels.map(hotel => hotel.storageBytes), 1);
    return Math.max((value / maximum) * 100, value > 0 ? 3 : 0);
  }

  async load() {
    try {
      this.loading = true;
      this.errorMessage = "";
      this.data = await SupportDashboard.getDashboard({
        periodDays: this.periodDays,
        hotelObjectId: this.hotelObjectId || undefined,
      });
    } catch (error) {
      this.errorMessage = "운영 지표를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.";
    } finally {
      this.loading = false;
    }
  }
}
